import React, { useState, useEffect, useContext } from 'react';
import { browserHistory, withRouter } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Typography, Spin, Modal, message } from 'antd';
import moment from 'moment/moment';

import styled from 'styled-components';

import { ApplicationUIContext } from '@yuhuinc/ui-library';

import TermsAndConditions from './wizard_steps/TermsAndConditions';
import ApplicationOverview from './wizard_steps/ApplicationOverview';
import ApplicationSummary from './wizard_steps/ApplicationSummary';
import DepositPayment from './wizard_steps/DepositPayment';
import StepItem from './wizard_steps/StepItem';
import { Wizard } from 'components/shared/Wizard';

import { getTemplateForPreview } from '../../landlord_app/application_templates/store/actions';
import {
  submitApplicantSubmission,
  saveApplicantSubmissionProgress,
  getApplicantSubmission,
} from './api/applicantSubmissions';
import { getCotenantApplicationTemplate } from './api/cotenantApplicationTemplates';
import { DUMMY_APPLICATION, DATE_FIELDS } from 'constants/tenantApplication.constants';
import { getTranslatedString } from 'helpers/i18n';
import ApplicationSubmission from './wizard_steps/ApplicationSubmission';

const { Title } = Typography;

const StyledSpinner = styled(Spin)`
  margin: auto;
  width: 100%;
  margin-top: 42px;
`;

// Mode can be one of:
// - preview: Only available from landlord app. It shows a preview of the template with all mutations disabled
// - edit: Only available from landlord app. It allows agents to edit tenant responses using the ApplicationTemplateWizard
// - defaults: Only available from tenant app. It shows the default ApplicationTemplateWizard for tenants to submit responses

const ApplicationTemplateWizard = ({
  applicationTemplateId,
  applicationId,
  mode = 'default',
  applicantId = null,
}) => {
  const { isMobile } = useContext(ApplicationUIContext);

  const queryClient = useQueryClient();

  const [applicationTemplate, setApplicationTemplate] = useState(null);
  const [application, setApplication] = useState(null);
  const [cotenant, setCotenant] = useState(null);
  const [steps, setSteps] = useState([]);
  const query = browserHistory.getCurrentLocation().query;
  const increaseButtonSizeOnMobile = { size: isMobile ? 'large' : 'default' };

  applicationId = applicationId ?? query.application_id;

  const isLandlordApp = mode !== 'default';
  const isPreview = mode === 'preview';
  const isEdit = mode === 'edit';

  const fetchTemplate = isLandlordApp
    ? () => getTemplateForPreview(applicationTemplateId)
    : () => getCotenantApplicationTemplate(applicationId);

  const formatBlockResponses = (responses) => {
    if (!responses) return [];

    return responses.map((response) => {
      const entries = Object.entries(response).map(([key, value]) => {
        return DATE_FIELDS[key] ? [key, moment(value)] : [key, value];
      });

      return Object.fromEntries(entries);
    });
  };

  const formatCotenant = (cotenant) => {
    return Object.fromEntries(
      Object.entries(cotenant).map(([key, value]) => {
        return DATE_FIELDS[key] ? [key, moment(value)] : [key, value];
      })
    );
  };

  const formatSteps = (steps, applicantSubmission) => {
    return steps.map((step) => {
      return {
        ...step,
        blocks: step.blocks.map((block) => {
          const submissionBlock = applicantSubmission?.blocks?.find(
            (submissionBlock) => submissionBlock.id === block.id
          );

          return {
            ...block,
            rendering_question_response: submissionBlock?.rendering_question_response,
            responses: formatBlockResponses(submissionBlock?.values),
          };
        }),
      };
    });
  };

  const formattedBlocksForSubmission = ({ steps, filterUndefinedValues = false }) => {
    const formattedBlocks = steps.reduce((acc, step, stepIndex) => {
      return acc.concat(
        step.blocks.map((block) => ({
          id: block.id,
          title: block.title,
          step_id: step.id,
          step_index: stepIndex,
          association: block.model_class_name,
          form_type: block.form_type,
          fields: block.fields,
          min_instances: block.min_instances,
          max_instances: block.max_instances,
          values: block.values ?? [],
          form: block.form ?? { valid: true, errors: [] },
          rendering_question: block.rendering_question,
          rendering_question_response: block.rendering_question_response,
        }))
      );
    }, []);

    if (filterUndefinedValues) {
      return formattedBlocks.map((block) => ({
        ...block,
        values: block.values.map((value) =>
          value.filter((value) => Object.values(value.attribute)[0] !== undefined)
        ),
      }));
    } else {
      return formattedBlocks;
    }
  };

  useEffect(() => {
    async function fetchData() {
      let applicantSubmission = [];
      const { application_template, application, cotenant = {} } = await fetchTemplate();

      // In preview mode there's no cotenant or submissions so we skip this step
      if (!isPreview) {
        const response = await getApplicantSubmission(applicationId, applicantId || cotenant.id);
        applicantSubmission = response.applicant_submission;
      }

      setCotenant(formatCotenant({ id: applicantId || cotenant.id, ...cotenant }));
      setApplicationTemplate(application_template || {});
      setApplication(application ?? DUMMY_APPLICATION);
      setSteps(formatSteps(application_template.steps, applicantSubmission ?? []));
    }

    fetchData();
  }, [applicationTemplateId]);

  function handlePageChange(stepId, blocks, isNext) {
    const updatedSteps = steps.map((step) => {
      if (step.id === stepId) {
        return { ...step, blocks };
      }
      return step;
    });

    const formattedBlocks = formattedBlocksForSubmission({
      steps: updatedSteps,
      filterUndefinedValues: true,
    });

    setSteps(updatedSteps);
    isNext && saveProgress(formattedBlocks);
  }

  const onClickCancel = () => {
    if (isPreview) return;

    Modal.confirm({
      title: getTranslatedString('tenantPortal.applicationTemplate.wizard.cancel.header'),
      content: getTranslatedString('tenantPortal.applicationTemplate.wizard.cancel.body'),
      onOk: () => {
        if (isEdit) {
          browserHistory.push(`/companies/applications/${applicationId}`);
        } else {
          browserHistory.push('/dashboard');
        }
      },
      okText: getTranslatedString('okay'),
      cancelText: getTranslatedString('cancel'),
      okButtonProps: increaseButtonSizeOnMobile,
      cancelButtonProps: increaseButtonSizeOnMobile,
      bodyStyle: { fontSize: isMobile ? 16 : 14 },
    });
  };

  const onClickSave = () => {
    if (isEdit) {
      browserHistory.push(`/companies/applications/${applicationId}`);
    } else {
      browserHistory.push('/dashboard');
    }
  };

  const validateForm = () => {
    const blocks = formattedBlocksForSubmission({
      steps: steps,
      filterUndefinedValues: true,
    });

    return blocks.every((block) => block.form.valid || block.fields.length === 0);
  };

  const saveProgress = (blocks) => {
    if (isPreview) return;

    saveApplicantSubmissionProgress({
      blocks,
      isLandlordApp,
      cotenantId: cotenant.id,
      applicationId: applicationId,
    })
      .then(() => {
        message.success('Step info saved');
      })
      .catch((error) => {
        message.error('Error occurred');
        console.log(error);
      });
  };

  const handleSubmit = (data = {}) => {
    Modal.confirm({
      title: 'Submit application',
      content: 'Once submitted, you will not be able to change your responses',
      onOk: () => {
        submitApplication(data);
      },
      okText: getTranslatedString('okay'),
      cancelText: getTranslatedString('cancel'),
      okButtonProps: increaseButtonSizeOnMobile,
      cancelButtonProps: increaseButtonSizeOnMobile,
      bodyStyle: { fontSize: isMobile ? 16 : 14 },
    });
  };

  const submitApplication = (data = {}) => {
    const isApplicationValid = validateForm();

    if (isPreview) {
      isApplicationValid
        ? message.success(
            getTranslatedString('tenantPortal.applicationTemplate.wizard.preview.success')
          )
        : message.error(
            getTranslatedString('tenantPortal.applicationTemplate.wizard.preview.error')
          );
    } else if (isApplicationValid) {
      submitApplicantSubmission({
        ...data,
        cotenantId: cotenant.id,
        applicationId: applicationId,
      })
        .then(() => {
          message.success('Success!');

          if (isEdit) {
            browserHistory.push(`/companies/applications/${applicationId}`);
          } else {
            queryClient.invalidateQueries({ queryKey: ['cotenant', cotenant.id] });
            browserHistory.push('/dashboard');
          }
        })
        .catch((error) => {
          message.error('Error occurred');
          console.log(error);
        });
    }
  };

  return (
    <>
      {!(applicationTemplate && application) ? (
        <StyledSpinner />
      ) : (
        <>
          <Title level={4}>
            {`${getTranslatedString('tenantPortal.applicationTemplate.wizard.title')} ${
              application.property.address.street
            }`}
            {mode === 'preview' ? ' (Fake address)' : null}
          </Title>
          <Wizard>
            {mode !== 'edit' && (
              <TermsAndConditions
                termsAndConditions={applicationTemplate.terms_and_conditions}
                consentText={applicationTemplate.consent_text}
                onClickCancel={onClickCancel}
              />
            )}
            {mode !== 'edit' && (
              <ApplicationOverview
                mode={mode}
                applicationTemplateOverviewTitle={applicationTemplate.application_overview_title}
                applicationTemplateOverviewText={applicationTemplate.application_overview_text}
                application={application}
                onClickCancel={onClickCancel}
              />
            )}
            {steps.map((step) => (
              <StepItem
                key={step.id}
                step={step}
                onPageChange={handlePageChange}
                applicationTemplate={applicationTemplate}
                onClickCancel={onClickCancel}
                application={application}
                cotenant={cotenant}
                isPreview={isPreview}
              />
            ))}
            <ApplicationSummary
              application={application}
              cotenant={cotenant}
              onClickCancel={onClickCancel}
              blocks={formattedBlocksForSubmission({ steps })}
              isPreview={isPreview}
            />
            <DepositPayment
              applicationTemplate={applicationTemplate}
              application={application}
              cotenantId={cotenant.id}
              onClickCancel={onClickCancel}
              isPreview={isPreview}
            />
            <ApplicationSubmission
              applicationTemplate={applicationTemplate}
              cotenant={cotenant}
              onSubmit={handleSubmit}
              onClickSave={onClickSave}
              isPreview={isPreview}
            />
          </Wizard>
        </>
      )}
    </>
  );
};

export default withRouter(ApplicationTemplateWizard);
